import { z } from 'zod'
import { SmartMenuRegistrationField } from '../../models/SmartMenuRegistrationField'
import type {
  SmartMenuAppSetting,
  SmartMenuThemeSetting,
  SmartMenuRegistrationConfigsSetting,
  SmartMenuRegistrationSetting,
  SmartMenuDigitalMenuConfigsSetting,
  SmartMenuDigitalMenuSetting,
  SmartMenuFunctionalitiesSetting
} from '../../models/AppSetting'

const SmartMenuThemeSettingDTOSchema = z.object({
  primary_color: z.string().optional(),
  secondary_color: z.string().optional(),
  logo: z.string().nullish()
})

const SmartMenuRegistrationConfigsDTOSchema = z.object({
  message_success_registration: z.string().optional(),
  show_qrcode: z.boolean().optional(),
  show_barcode: z.boolean().optional()
})

const SmartMenuRegistrationDTOSchema = z.object({
  active: z.boolean().optional(),
  url: z.string().nullish(),
  fields: z.array(z.nativeEnum(SmartMenuRegistrationField)).optional(),
  configs: SmartMenuRegistrationConfigsDTOSchema.optional()
})

const SmartMenuDigitalMenuConfigsDTOSchema = z.object({
  show_image: z.boolean().optional(),
  show_price_symbol: z.boolean().optional(),
  show_decimal: z.boolean().optional(),
  enable_order: z.boolean().optional(),
  enable_service_tax: z.boolean().optional(),
  enable_table_order: z.boolean().optional(),
  require_table_number: z.boolean().optional(),
  enable_customer_service: z.boolean().optional(),
  order_needs_customer_service_opened: z.boolean().optional(),
  show_request_confirmation: z.boolean().optional(),
  enable_subcategories: z.boolean().optional(),
  request_confirmation_message: z.string().optional(),
  request_confirmation_confirm_button_text: z.string().optional(),
  request_confirmation_close_button_text: z.string().optional(),
  reorder_message: z.string().optional()
})

const SmartMenuDigitalMenuDTOSchema = z.object({
  active: z.boolean().optional(),
  url: z.string().nullish(),
  configs: SmartMenuDigitalMenuConfigsDTOSchema.optional()
})

const SmartMenuFunctionalitiesDTOSchema = z.object({
  registration: SmartMenuRegistrationDTOSchema.optional(),
  digitalmenu: SmartMenuDigitalMenuDTOSchema.optional()
})

export const SmartMenuAppSettingDTO = z.object({
  page_title: z.string().optional(),
  smart_menu_url: z.string().optional(),
  theme: SmartMenuThemeSettingDTOSchema.optional(),
  functionalities: SmartMenuFunctionalitiesDTOSchema.optional()
})

type SmartMenuThemeSettingDTO = z.infer<typeof SmartMenuThemeSettingDTOSchema>
type SmartMenuRegistrationConfigsDTO = z.infer<typeof SmartMenuRegistrationConfigsDTOSchema>
type SmartMenuRegistrationDTO = z.infer<typeof SmartMenuRegistrationDTOSchema>
type SmartMenuDigitalMenuConfigsDTO = z.infer<typeof SmartMenuDigitalMenuConfigsDTOSchema>
type SmartMenuDigitalMenuDTO = z.infer<typeof SmartMenuDigitalMenuDTOSchema>
type SmartMenuFunctionalitiesDTO = z.infer<typeof SmartMenuFunctionalitiesDTOSchema>
export type SmartMenuAppSettingDTO = z.infer<typeof SmartMenuAppSettingDTO>

function normalizeHexColor (color: string | undefined): string | undefined {
  if (!color) { return undefined }
  const cleaned = color.replace('#', '')
  return cleaned ? `#${cleaned}` : undefined
}

function mapThemeToDTO (model: SmartMenuThemeSetting): SmartMenuThemeSettingDTO {
  return {
    primary_color: normalizeHexColor(model.primaryColor),
    secondary_color: normalizeHexColor(model.secondaryColor),
    logo: model.logo
  }
}

function mapRegistrationConfigsToDTO (model: SmartMenuRegistrationConfigsSetting): SmartMenuRegistrationConfigsDTO {
  return {
    message_success_registration: model.messageSuccessRegistration,
    show_qrcode: model.showQrcode,
    show_barcode: model.showBarcode
  }
}

function mapRegistrationToDTO (model: SmartMenuRegistrationSetting): SmartMenuRegistrationDTO {
  return {
    active: model.active,
    url: model.url,
    fields: model.fields,
    configs: model.configs ? mapRegistrationConfigsToDTO(model.configs) : undefined
  }
}

function mapDigitalMenuConfigsToDTO (model: SmartMenuDigitalMenuConfigsSetting): SmartMenuDigitalMenuConfigsDTO {
  return {
    show_image: model.showImage,
    show_price_symbol: model.showPriceSymbol,
    show_decimal: model.showDecimal,
    enable_order: model.enableOrder,
    enable_service_tax: model.enableServiceTax,
    enable_table_order: model.enableTableOrder,
    require_table_number: model.requireTableNumber,
    enable_customer_service: model.enableCustomerService,
    order_needs_customer_service_opened: model.orderNeedsCustomerServiceOpened,
    show_request_confirmation: model.showRequestConfirmation,
    enable_subcategories: model.enableSubcategories,
    request_confirmation_message: model.requestConfirmationMessage,
    request_confirmation_confirm_button_text: model.requestConfirmationConfirmButtonText,
    request_confirmation_close_button_text: model.requestConfirmationCloseButtonText,
    reorder_message: model.reorderMessage
  }
}

function mapDigitalMenuToDTO (model: SmartMenuDigitalMenuSetting): SmartMenuDigitalMenuDTO {
  return {
    active: model.active,
    url: model.url,
    configs: model.configs ? mapDigitalMenuConfigsToDTO(model.configs) : undefined
  }
}

function mapFunctionalitiesToDTO (model: SmartMenuFunctionalitiesSetting): SmartMenuFunctionalitiesDTO {
  return {
    registration: model.registration ? mapRegistrationToDTO(model.registration) : undefined,
    digitalmenu: model.digitalmenu ? mapDigitalMenuToDTO(model.digitalmenu) : undefined
  }
}

export function mapSmartMenuAppSettingToDTO (model: SmartMenuAppSetting): SmartMenuAppSettingDTO {
  return {
    page_title: model.pageTitle,
    smart_menu_url: model.smartMenuUrl,
    theme: model.theme ? mapThemeToDTO(model.theme) : undefined,
    functionalities: model.functionalities ? mapFunctionalitiesToDTO(model.functionalities) : undefined
  }
}

function mapDTOToTheme (dto: SmartMenuThemeSettingDTO): SmartMenuThemeSetting {
  return {
    primaryColor: dto.primary_color,
    secondaryColor: dto.secondary_color,
    logo: dto.logo ?? undefined
  }
}

function mapDTOToRegistrationConfigs (dto: SmartMenuRegistrationConfigsDTO): SmartMenuRegistrationConfigsSetting {
  return {
    messageSuccessRegistration: dto.message_success_registration,
    showQrcode: dto.show_qrcode,
    showBarcode: dto.show_barcode
  }
}

function mapDTOToRegistration (dto: SmartMenuRegistrationDTO): SmartMenuRegistrationSetting {
  return {
    active: dto.active,
    url: dto.url ?? undefined,
    fields: dto.fields,
    configs: dto.configs ? mapDTOToRegistrationConfigs(dto.configs) : undefined
  }
}

function mapDTOToDigitalMenuConfigs (dto: SmartMenuDigitalMenuConfigsDTO): SmartMenuDigitalMenuConfigsSetting {
  return {
    showImage: dto.show_image,
    showPriceSymbol: dto.show_price_symbol,
    showDecimal: dto.show_decimal,
    enableOrder: dto.enable_order,
    enableServiceTax: dto.enable_service_tax,
    enableTableOrder: dto.enable_table_order,
    requireTableNumber: dto.require_table_number,
    enableCustomerService: dto.enable_customer_service,
    orderNeedsCustomerServiceOpened: dto.order_needs_customer_service_opened,
    showRequestConfirmation: dto.show_request_confirmation,
    enableSubcategories: dto.enable_subcategories,
    requestConfirmationMessage: dto.request_confirmation_message,
    requestConfirmationConfirmButtonText: dto.request_confirmation_confirm_button_text,
    requestConfirmationCloseButtonText: dto.request_confirmation_close_button_text,
    reorderMessage: dto.reorder_message
  }
}

function mapDTOToDigitalMenu (dto: SmartMenuDigitalMenuDTO): SmartMenuDigitalMenuSetting {
  return {
    active: dto.active,
    url: dto.url ?? undefined,
    configs: dto.configs ? mapDTOToDigitalMenuConfigs(dto.configs) : undefined
  }
}

function mapDTOToFunctionalities (dto: SmartMenuFunctionalitiesDTO): SmartMenuFunctionalitiesSetting {
  return {
    registration: dto.registration ? mapDTOToRegistration(dto.registration) : undefined,
    digitalmenu: dto.digitalmenu ? mapDTOToDigitalMenu(dto.digitalmenu) : undefined
  }
}

export function mapDTOToSmartMenuAppSetting (dto: SmartMenuAppSettingDTO): SmartMenuAppSetting {
  return {
    pageTitle: dto.page_title,
    smartMenuUrl: dto.smart_menu_url,
    theme: dto.theme ? mapDTOToTheme(dto.theme) : undefined,
    functionalities: dto.functionalities ? mapDTOToFunctionalities(dto.functionalities) : undefined
  }
}
